import { useState } from 'react';
import { Search, Eye, CreditCard, XCircle, CheckCircle, Clock, Loader2, Music, QrCode } from 'lucide-react';
import { useStore } from '../store/useStore';
import type { OrderStatus } from '../types';
import Modal from '../components/Modal';
import { toast } from '../components/Toast';

const tabs: { label: string; value: OrderStatus | 'all' }[] = [
  { label: '全部订单', value: 'all' }, { label: '待支付', value: 'pending' }, { label: '处理中', value: 'processing' }, { label: '已完成', value: 'completed' }, { label: '已关闭', value: 'closed' },
];

const stCfg: Record<OrderStatus, { color: string; bg: string; icon: typeof CheckCircle; label: string }> = {
  pending: { color: 'text-warning-dark', bg: 'bg-warning-50', icon: Clock, label: '待支付' },
  processing: { color: 'text-brand', bg: 'bg-brand-50', icon: Loader2, label: '处理中' },
  completed: { color: 'text-success-dark', bg: 'bg-success-50', icon: CheckCircle, label: '已完成' },
  closed: { color: 'text-text-muted', bg: 'bg-slate-100', icon: XCircle, label: '已关闭' },
};

export default function OrderManagement() {
  const { orders, updateOrder } = useStore();
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [selId, setSelId] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');

  const filtered = orders.filter(o => (activeTab === 'all' || o.status === activeTab) && (o.orderNo.includes(search) || o.danceName.toLowerCase().includes(search.toLowerCase())));
  const sel = orders.find(o => o.id === selId);

  const handleCancel = (id: string) => { updateOrder(id, { status: 'closed', closeReason: '用户取消' }); setShowCancel(null); toast('success', '订单已取消'); };

  const paymentMethods = [
    { id: 'wechat', name: '微信支付', icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/WeChat_logo.svg/1024px-WeChat_logo.svg.png', color: 'border-success focus:ring-success/20' },
    { id: 'alipay', name: '支付宝', icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Alipay_logo.svg/1024px-Alipay_logo.svg.png', color: 'border-brand focus:ring-brand/20' },
    { id: 'unionpay', name: '银联支付', icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/UnionPay_logo.svg/1024px-UnionPay_logo.svg.png', color: 'border-warning focus:ring-warning/20' },
  ];

  const handleContinuePay = (id: string) => {
    setSelId(id);
    setPaymentMethod('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = () => {
    if (!paymentMethod) { toast('warning', '请选择支付方式'); return; }
    setShowPaymentModal(false);
    setShowQRModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowQRModal(false);
    if (selId) updateOrder(selId, { status: 'processing' });
    setShowSuccessModal(true);
  };

  const handleSuccessOk = () => { setShowSuccessModal(false); setSelId(null); };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-bold text-text-primary font-display">订单管理</h1><p className="text-sm text-text-secondary mt-1.5">查看和管理您的所有订单</p></div>
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索订单号或舞蹈名称..."
            className="pl-10 pr-4 py-2.5 w-64 border border-border-main rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand shadow-soft" />
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(t => (
          <button key={t.value} onClick={() => setActiveTab(t.value)}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${activeTab === t.value ? 'gradient-brand text-white shadow-md shadow-brand/15' : 'bg-white text-text-secondary border border-border-subtle hover:border-brand/30 hover:text-brand shadow-soft'}`}>
            {t.label}
            <span className={`ml-1.5 text-[11px] tabular-nums ${activeTab === t.value ? 'text-white/60' : 'text-text-muted'}`}>({t.value === 'all' ? orders.length : orders.filter(o => o.status === t.value).length})</span>
          </button>
        ))}
      </div>

      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(order => {
            const c = stCfg[order.status]; const Icon = c.icon;
            return (
              <div key={order.id} className="bg-white rounded-2xl shadow-soft p-5 hover:shadow-elevated transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-text-muted">订单号：<span className="text-text-primary font-mono font-medium">{order.orderNo}</span></span>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg ${c.bg} ${c.color}`}><Icon size={12} className="inline mr-0.5" />{c.label}</span>
                  </div>
                  <span className="text-xl font-bold text-text-primary font-display">￥{order.amount}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[['类型', order.typeLabel], ['创建时间', order.createdAt], ['舞蹈名称', order.danceName], ['机型', order.robotModel]].map(([l, v]) => (
                    <div key={l as string}><p className="text-[11px] text-text-muted mb-0.5">{l}</p><p className="text-sm text-text-primary font-medium">{v}</p></div>
                  ))}
                </div>
                {order.closeReason && <p className="text-xs text-text-muted mb-4 bg-slate-50 rounded-xl px-4 py-2.5">关闭原因：{order.closeReason}</p>}
                <div className="flex items-center gap-2.5 pt-4 border-t border-border-subtle">
                  {order.status === 'pending' && (
                    <>
                      <button onClick={() => handleContinuePay(order.id)} className="flex items-center gap-1.5 px-4 py-2 gradient-brand text-white rounded-xl text-xs font-semibold hover-lift"><CreditCard size={13} /> 继续支付</button>
                      <button onClick={() => setShowCancel(order.id)} className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-text-secondary rounded-xl text-xs font-medium hover:bg-slate-200 transition-colors">取消订单</button>
                    </>
                  )}
                  <div className="flex-1" />
                  <button onClick={() => { setSelId(order.id); setShowDetail(true); }} className="text-xs text-text-muted hover:text-brand transition-colors font-medium">查看详情 →</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24"><div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4"><Music size={32} className="text-text-muted" /></div><p className="text-sm text-text-secondary font-medium">暂无订单</p></div>
      )}

      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="订单详情" width="max-w-lg">
        {sel && (
          <div className="space-y-3">
            {[['订单号', sel.orderNo], ['状态', stCfg[sel.status].label], ['类型', sel.typeLabel], ['金额', `￥${sel.amount}`], ['创建时间', sel.createdAt], ['舞蹈名称', sel.danceName], ['机型', sel.robotModel]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3"><span className="text-xs text-text-muted">{l}</span><span className="text-sm text-text-primary font-medium">{v}</span></div>
            ))}
            {sel.closeReason && <div className="bg-danger-50 rounded-xl px-4 py-3 flex items-center justify-between"><span className="text-xs text-danger">关闭原因</span><span className="text-sm text-danger font-medium">{sel.closeReason}</span></div>}
          </div>
        )}
      </Modal>

      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="确认支付" width="max-w-sm">
        <div className="text-center mb-6">
          <p className="text-xs text-text-muted mb-1">支付金额</p>
          <p className="text-4xl font-bold text-text-primary font-display">￥{sel?.amount ?? 0}</p>
        </div>
        <p className="text-xs text-text-secondary font-semibold mb-3">选择支付方式</p>
        <div className="space-y-2.5 mb-6">
          {paymentMethods.map(pm => (
            <label key={pm.id} className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${paymentMethod === pm.id ? `${pm.color} bg-white shadow-sm` : 'border-border-main hover:border-slate-300'}`}>
              <input type="radio" name="pay" checked={paymentMethod === pm.id} onChange={() => setPaymentMethod(pm.id)} className="text-brand w-4 h-4" />
              <img src={pm.icon} alt={pm.name} className="w-6 h-6 object-contain" />
              <span className="text-sm font-medium text-text-primary">{pm.name}</span>
            </label>
          ))}
        </div>
        <button onClick={handleConfirmPayment} disabled={!paymentMethod} className="w-full py-3.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift disabled:opacity-50 flex items-center justify-center gap-2">
          <CreditCard size={16} /> 确认支付 ￥{sel?.amount ?? 0}
        </button>
        <button onClick={() => setShowPaymentModal(false)} className="w-full py-2 text-sm text-text-muted hover:text-text-secondary mt-2 transition-colors">取消</button>
      </Modal>

      <Modal isOpen={showQRModal} onClose={() => setShowQRModal(false)} title="扫码支付" width="max-w-xs" hideClose>
        <div className="text-center">
          <p className="text-sm text-text-secondary mb-4">请使用<strong className="text-text-primary">{paymentMethod === 'wechat' ? '微信' : paymentMethod === 'alipay' ? '支付宝' : '银联'}</strong>扫码支付</p>
          <div className="bg-white rounded-xl p-6 border border-border-subtle inline-block mb-4">
            <div className="w-44 h-44 bg-slate-50 rounded-lg flex items-center justify-center">
              <QrCode size={120} className="text-text-primary" />
            </div>
          </div>
          <p className="text-xs text-text-muted mb-5">支付金额：<strong className="text-brand">￥{sel?.amount ?? 0}</strong></p>
          <button onClick={handlePaymentSuccess} className="w-full py-3 bg-success text-white rounded-xl text-sm font-semibold hover:bg-success-dark transition-colors flex items-center justify-center gap-2">
            我已完成支付
          </button>
          <button onClick={() => { setShowQRModal(false); setShowPaymentModal(true); }} className="w-full py-2 text-xs text-text-muted hover:text-text-secondary mt-2">遇到问题？更换支付方式</button>
        </div>
      </Modal>

      <Modal isOpen={showSuccessModal} onClose={handleSuccessOk} title="支付成功" width="max-w-sm" hideClose>
        <div className="text-center py-6">
          <div className="w-20 h-20 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={40} className="text-success" />
          </div>
          <h3 className="text-lg font-bold text-text-primary mb-2 font-display">支付成功！</h3>
          <p className="text-sm text-text-muted mb-1">订单「{sel?.danceName}」已提交处理</p>
          <p className="text-xs text-text-muted">您可以在订单详情中查看处理进度</p>
          <button onClick={handleSuccessOk} className="mt-6 px-8 py-3 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
            确定
          </button>
        </div>
      </Modal>

      <Modal isOpen={!!showCancel} onClose={() => setShowCancel(null)} title="取消订单">
        <div className="text-center py-2"><div className="w-14 h-14 bg-danger-50 rounded-2xl flex items-center justify-center mx-auto mb-4"><XCircle size={24} className="text-danger" /></div><p className="text-sm text-text-primary font-semibold">确定取消此订单？</p><p className="text-xs text-text-muted mt-1">取消后订单将关闭</p></div>
        <div className="flex gap-3 mt-5">
          <button onClick={() => setShowCancel(null)} className="flex-1 py-2.5 text-sm bg-slate-100 rounded-xl font-medium hover:bg-slate-200 transition-colors">再想想</button>
          <button onClick={() => handleCancel(showCancel!)} className="flex-1 py-2.5 text-sm text-white bg-danger rounded-xl font-medium hover:bg-danger-dark transition-colors">确认取消</button>
        </div>
      </Modal>


    </div>
  );
}
